import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ElementRef, Injectable } from '@angular/core';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { loadStripe } from '@stripe/stripe-js';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import * as html2pdf from 'html2pdf.js';
import { BaseHttpService } from '../auth/base-capacitor-http';
import { AuthResponse } from '../auth/auth.service';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { Preferences } from '@capacitor/preferences';
import { MatSnackBar } from '@angular/material/snack-bar';

export class Receipt {
  email: string;
  ref: string;
  orderId: number;
  subtotal: number;
  total: number;
  discount: number;
  tax: number;
  status: string;
  shipping: number;
  orderDate: Date;
  name: string;
  orderItems: OrderItem[];
  periodStart: number;
  periodEnd: number;
  effectiveAt: number;
  number: string;

}

export class OrderItem {
  quantity: number;
  shipped: number;
  product: ProductDisplay;

}

export class SubData {
  active: boolean;
  expires: number;

}

export class Subscription {
  id: string;
  date: Date;
  status: string;
  paymentMethod: string;
  items: SubItem[];
  total: number;
}

export class SubItem {
  name: string;
  quantity: number;
  price: number;
}
export class CheckoutSession {
  id: string;
  sid?: string;
  line_items: CheckoutLineItem[];
  mode: 'payment';
  success_url: string;
  cancel_url: string;
}

export class CheckoutLineItem {
  currency: 'usd';
  price_id: string;
  description: string;
  name: string;
  images: string[]
  unit_amount: number;
  quantity: number;
}

export class ProductDisplay {
  name: string;
  description: string;
  amount: number;
  interval: string;
  currency: string;
  type: string;
  price_id: string;
  img: string;
  features: string[];
}

@Injectable({
  providedIn: 'root'
})
export class PaymentService extends BaseHttpService {

  private readonly PROMO_KEY = 'PROMO';
  private baseURL: string;
  private urlEndPoint: string;

  constructor(client: HttpClient, snackbar: MatSnackBar) {
    super(client, snackbar);
    this.baseURL = environment.server.mainURL;
  }

  private async getStripePk(): Promise<{ pk: string }> {
    this.urlEndPoint = this.baseURL + 'finance/pk';
    return this.get<{ pk: string }>(this.urlEndPoint);
  }

  async addPromoCode(promo: string) {
    if (Capacitor.isNativePlatform()) {
      try {
        await SecureStoragePlugin.set({ key: this.PROMO_KEY, value: promo });
      } catch {
        return;
      }
      return;
    }
    const tokenResponse: string = this.encryptPayLoad<string>(promo, this.KEY);
    await Preferences.set({ key: this.AUTH_TOKEN, value: tokenResponse });
  }

  async getPromo(): Promise<string> {
    if (Capacitor.isNativePlatform()) {
      try {
        const response = await SecureStoragePlugin.get({ key: this.PROMO_KEY });
        return response.value;
      } catch (err) {
        return null;
      }
    }

    const { value } = await Preferences.get({ key: this.PROMO_KEY });
    if (value) {
      let response = this.decryptPayload<string>(value, this.KEY);
      return response;
    }
    return null;
  }

  async removePromo() {
    if (Capacitor.isNativePlatform()) {
      await SecureStoragePlugin.remove({ key: this.PROMO_KEY });
      return;
    }
    await Preferences.remove({ key: this.PROMO_KEY });
  }


  async getCheckoutSession(product: ProductDisplay) {

    this.urlEndPoint = this.baseURL + 'finance/checkout';
    const promo: string = await this.getPromo();
    let body = {
      success_url: environment.server.domain + 'success?session_id={CHECKOUT_SESSION_ID}',// as per documentation
      cancel_url: environment.server.domain + 'cart-view',
      price_id: product.price_id,
      promo: promo,
      name: product.name,
      url: product.img
    }
    return this.post<CheckoutSession>(this.urlEndPoint, body);
  }

  async checkout(sessionId: string) {
    const { pk } = await this.getStripePk();
    const stripe = await loadStripe(pk);

    return stripe.redirectToCheckout({ sessionId: sessionId });
  }

  async openCustomerPortal() {
    const res = await this.customerPortal();
    window.location.href = res.url;
  }

  private async customerPortal() {
    this.urlEndPoint = this.baseURL + 'finance/billing-portal';
    let body = {
      returnUrl: environment.server.domain + 'settings'
    };

    return this.post<{ url: string }>(this.urlEndPoint, body);
  }


  downLoadReceipt(element: ElementRef) {

    const options = {
      margin: 10,
      filename: 'receipt.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true, // Important if you have images or fonts from external sources
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
      }
    };

    // Ensure the element exists
    if (!element?.nativeElement) {
      console.error('ElementRef is invalid or not found');
      return;
    }

    html2pdf()
      .set(options)
      .from(element.nativeElement)
      .save()
      .catch(err => {
        console.error('PDF generation error:', err);
      });
  }

  async emailPdf(element: ElementRef) {
    const options = {
      margin: 10,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    const worker = html2pdf()
      .set(options)
      .from(element.nativeElement);

    const blob: Blob = await worker.outputPdf('blob');
    const formData = new FormData();
    formData.append('file', blob, 'receipt.pdf');

    this.urlEndPoint = this.baseURL + 'finance/email-receipt'
    const headers = await this.getAuthHeaders();
    if (Capacitor.isNativePlatform()) {
      const res = await CapacitorHttp.post({ url: this.urlEndPoint, data: formData, headers: headers });
      return res.data as boolean;
    }

    return lastValueFrom(this.client.post<boolean>(this.urlEndPoint, formData, { headers: new HttpHeaders(headers) }));

  }

  async getUserPayHistory() {
    this.urlEndPoint = this.baseURL + 'finance/receipt';
    return this.get<Subscription>(this.urlEndPoint);
  }

  async getReceipt(sessionId: string) {
    this.urlEndPoint = this.baseURL + 'finance/session-receipt';
    return this.get<Receipt>(this.urlEndPoint, { session_id: sessionId });

  }

  async checkForActiveSubscription() {
    this.urlEndPoint = this.baseURL + 'finance/check-subscription';
    return this.get<AuthResponse>(this.urlEndPoint);
  }

  async getProducts(): Promise<ProductDisplay[]> {
    this.urlEndPoint = this.baseURL + 'commons/product-info';
    return this.get<ProductDisplay[]>(this.urlEndPoint);
  }

  async cancelCustomerActiveSubscription(subId: string, type: string) {
    this.urlEndPoint = this.baseURL + 'finance/subscription/' + subId;
    let body = {
      type: type,
    };

    return this.put<Subscription>(this.urlEndPoint, body);
  }

  async getCustomerActiveSubscription(): Promise<ProductDisplay[]> {
    this.urlEndPoint = this.baseURL + 'finance/subscription-all';
    return this.get<ProductDisplay[]>(this.urlEndPoint);
  }


}
